import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Star, Flame, Target, Zap, Heart, Crown, Sparkles } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useGameSounds } from '@/hooks/useGameSounds';

interface AchievementUnlockProps {
  achievement: {
    name: string;
    description: string;
    icon: string;
    points: number;
    tier: 'bronze' | 'silver' | 'gold' | 'platinum';
  } | null;
  onClose: () => void;
}

const ICON_MAP: Record<string, LucideIcon> = {
  trophy: Trophy,
  star: Star,
  flame: Flame,
  target: Target,
  zap: Zap,
  heart: Heart,
  crown: Crown,
  sparkles: Sparkles,
};

const TIER_COLORS = {
  bronze: {
    bg: 'from-amber-700/20 to-amber-900/20',
    border: 'border-amber-600/50',
    icon: 'text-amber-500',
    glow: 'shadow-amber-500/30',
  },
  silver: {
    bg: 'from-muted/20 to-muted/40',
    border: 'border-muted/50',
    icon: 'text-muted',
    glow: 'shadow-muted/30',
  },
  gold: {
    bg: 'from-yellow-500/20 to-amber-500/20',
    border: 'border-yellow-500/50',
    icon: 'text-yellow-400',
    glow: 'shadow-yellow-400/40',
  },
  platinum: {
    bg: 'from-cyan-400/20 to-blue-500/20',
    border: 'border-cyan-400/50',
    icon: 'text-cyan-300',
    glow: 'shadow-cyan-400/50',
  },
};

export default function AchievementUnlock({ achievement, onClose }: AchievementUnlockProps) {
  const [particles, setParticles] = useState<Array<{ id: number; angle: number; distance: number }>>([]);
  const { playSound } = useGameSounds();

  useEffect(() => {
    if (achievement) {
      // Play achievement sound
      playSound('achievement');
      
      // Generate particles for burst effect
      const newParticles = Array.from({ length: 12 }, (_, i) => ({
        id: i,
        angle: (i * 30) + Math.random() * 15,
        distance: 60 + Math.random() * 40,
      }));
      setParticles(newParticles);

      const timer = setTimeout(onClose, 4000);
      return () => clearTimeout(timer);
    }
  }, [achievement, onClose, playSound]);

  if (!achievement) return null;

  const IconComponent = ICON_MAP[achievement.icon] || Trophy;
  const tierStyle = TIER_COLORS[achievement.tier];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          exit={{ scale: 0, rotate: 180 }}
          transition={{ type: 'spring', stiffness: 200, damping: 15 }}
          className={`relative p-8 rounded-2xl bg-gradient-to-br ${tierStyle.bg} ${tierStyle.border} border-2 shadow-2xl ${tierStyle.glow}`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Particle burst */}
          {particles.map((particle) => (
            <motion.div
              key={particle.id}
              initial={{ scale: 0, x: 0, y: 0 }}
              animate={{
                scale: [0, 1, 0],
                x: Math.cos(particle.angle * Math.PI / 180) * particle.distance,
                y: Math.sin(particle.angle * Math.PI / 180) * particle.distance,
              }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className={`absolute top-1/2 left-1/2 w-2 h-2 rounded-full ${tierStyle.icon} bg-current`}
            />
          ))}

          {/* Glow rings */}
          <motion.div
            animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
            className={`absolute inset-0 rounded-2xl ${tierStyle.border} border-2`}
          />

          <div className="text-center relative z-10">
            {/* Icon */}
            <motion.div
              animate={{ 
                y: [0, -10, 0],
                rotate: [0, 5, -5, 0],
              }}
              transition={{ duration: 2, repeat: Infinity }}
              className={`w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br ${tierStyle.bg} flex items-center justify-center shadow-lg ${tierStyle.glow}`}
            >
              <IconComponent className={`w-10 h-10 ${tierStyle.icon}`} />
            </motion.div>

            {/* Badge */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="mb-2"
            >
              <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${tierStyle.icon} bg-current/10`}>
                {achievement.tier} Achievement
              </span>
            </motion.div>

            {/* Title */}
            <motion.h3
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-2xl font-bold text-foreground mb-2"
            >
              {achievement.name}
            </motion.h3>

            {/* Description */}
            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-muted-foreground mb-4 max-w-xs"
            >
              {achievement.description}
            </motion.p>

            {/* Points */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.6, type: 'spring' }}
              className="flex items-center justify-center gap-2"
            >
              <Sparkles className="w-5 h-5 text-primary" />
              <span className="text-xl font-bold text-primary">+{achievement.points} XP</span>
              <Sparkles className="w-5 h-5 text-primary" />
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
